import React, { useState, useEffect, useRef } from 'react';
import { parseISO } from 'date-fns';
import { fetchBalance, fetchEntries, fetchBalanceEntries } from '../../services/dataService';
import { useTranslation } from 'react-i18next';
import LoadingScreen from "./loadingScreen";
import DateRangeWithLine from "./DateSelector"; // Brought the DateSelector here!
import {
  calculateLedgerBalance,
  getTransactionContribution,
} from '../../utils/balanceCalculation';

const formatSignedCurrency = (amount) =>
  `${amount >= 0 ? '+' : '−'} ₹${Math.abs(amount).toFixed(2)}`;

const DairyFinancialReport = (props) => {
  const { t, i18n } = useTranslation();
  const formatReportDate = (value) =>
    new Intl.DateTimeFormat(
      i18n.resolvedLanguage?.startsWith('hi') ? 'hi-IN' : 'en-IN',
      { day: '2-digit', month: 'short', year: 'numeric' },
    ).format(typeof value === 'string' ? parseISO(value) : value);
  // State for dates now lives inside the report
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  
  const [milkTableData, setMilkTableData] = useState({});
  const [balanceData, setBalanceData] = useState(null);
  const [loading, setLoading] = useState(false); // Default false until dates are ready
  const [error, setError] = useState(null);
  const [borrow, setBorrowData] = useState([]);
  const requestIdRef = useRef(0);
  
  const { userId, userName, userMobile } = props;

  const PRINT_STYLES = `
  <style>
    body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; margin: 0; padding: 20px; color: #1F3A5F; background: #fff; }
    .print-header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #E1DBC9; padding-bottom: 15px; }
    .report-title { font-size: 22px; font-weight: bold; color: #1F3A5F; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 1px; }
    .report-details { display: flex; justify-content: center; gap: 20px; flex-wrap: wrap; margin-bottom: 10px; font-size: 12px; color: #6B7280; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 25px; page-break-inside: avoid; font-size: 11px; }
    th, td { border: 1px solid #E1DBC9; padding: 8px 6px; text-align: left; }
    th { background-color: #F6F3EC; font-weight: bold; text-transform: uppercase; letter-spacing: 0.5px; }
    .text-right { text-align: right; }
    .text-center { text-align: center; }
    .credit { color: #10B981; font-weight: bold; }
    .debit { color: #EF4444; font-weight: bold; }
    .bold-row td { font-weight: bold; background-color: #F6F3EC; }
    h2 { color: #1F3A5F; font-size: 14px; margin: 20px 0 10px; text-transform: uppercase; letter-spacing: 1px; }
    @page { size: auto; margin: 10mm; }
    @media print { body { padding: 0; } .no-print { display: none !important; } }
  </style>
  `;

  const fetchData = async () => {
    if (!startDate || !endDate || !userId) return; // Prevent fetch until dates exist
    const requestId = ++requestIdRef.current;

    try {
      setLoading(true);
      setError(null);

      const [entriesData, nextBalanceData, sheetData] = await Promise.all([
        fetchEntries(startDate, endDate, userId),
        fetchBalance(startDate, endDate, userId),
        fetchBalanceEntries(startDate, endDate, userId),
      ]);

      if (requestId !== requestIdRef.current) return;

      const { morningEntries, eveningEntries } = entriesData;
      setBorrowData(sheetData?.borrow || []);

      const processMilkEntries = (entries, session) => 
        entries.map(entry => ({
          id: entry.id,
          type: "milk",
          date: entry.date,
          session: session,
          weight: parseFloat(entry.weight),
          fat: parseFloat(entry.fat),
          ratePerLtr: parseFloat(entry.price),
          amount: parseFloat(entry.total)
        }));

      const allMilkEntries = [
        ...processMilkEntries(eveningEntries, "evening"),
        ...processMilkEntries(morningEntries, "morning")
      ];

      const milkGroups = {};
      allMilkEntries.forEach(entry => {
        const key = entry.date + '|' + entry.session;
        if (!milkGroups[key]) {
          milkGroups[key] = {
            date: entry.date,
            session: entry.session,
            totalWeight: 0,
            totalAmount: 0,
            weightedFat: 0,
          };
        }
        milkGroups[key].totalWeight += entry.weight;
        milkGroups[key].totalAmount += entry.amount;
        milkGroups[key].weightedFat += entry.weight * entry.fat;
      });

      const milkByDate = {};
      Object.values(milkGroups).forEach(group => {
        const date = group.date;
        if (!milkByDate[date]) milkByDate[date] = [];
        milkByDate[date].push({
          ...group,
          avgFat: group.weightedFat / group.totalWeight,
          ratePerLtr: group.totalAmount / group.totalWeight
        });
      });

      Object.keys(milkByDate).forEach(date => {
        milkByDate[date].sort((a, b) => 
          a.session === 'morning' ? -1 : b.session === 'morning' ? 1 : 0
        );
      });

      setBalanceData(nextBalanceData);
      setMilkTableData(milkByDate);
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      console.error("Fetch error:", error);
      setError(error.message);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const getTransactionDesc = (transaction) => {
    const item = String(transaction.item || '').trim().toLowerCase();
    const rawName = String(transaction.name || '').trim();
    const displayName = rawName.toLowerCase() === 'dairy'
      ? t('adminBalance.dairy')
      : rawName;

    if (item === 'money given' || item === 'give money') {
      return displayName
        ? t('report.desc_templates.given_to', { name: displayName })
        : t('report.desc_templates.given_generic');
    } 
    else if (['money received', 'money recieved', 'receive money'].includes(item)) {
      return displayName
        ? t('report.desc_templates.rec_from', { name: displayName })
        : t('report.desc_templates.rec_generic');
    } 
    else if (item === 'feed') {
      return displayName
        ? t('report.desc_templates.purchased_from', {
            item: t('balanceSheet.items.feed'),
            name: displayName,
          })
        : t('balanceSheet.items.feed');
    }
    else if (item === 'ghee') {
      return displayName
        ? t('report.desc_templates.purchased_from', {
            item: t('balanceSheet.items.ghee'),
            name: displayName,
          })
        : t('balanceSheet.items.ghee');
    }
    else if (displayName) {
      return t('report.desc_templates.purchased_from', { item: transaction.item, name: displayName });
    }
    return transaction.item; 
  };

  const handlePrint = async () => {
    const hasBridge = window.AndroidBridge && typeof window.AndroidBridge.printHtml === 'function';
    const printLedger = calculateLedgerBalance({
      openingBalance: balanceData?.Before?.total,
      milk: balanceData?.milk?.total,
      feed: balanceData?.feed?.totalMoney,
      ghee: balanceData?.ghee?.totalMoney,
      moneyReceived: balanceData?.moneyReceived?.totalMoney,
      moneyGiven: balanceData?.moneyGiven?.totalMoney,
    });
    
    const milkRows = sortedDates.map(date => {
        const sessions = milkTableData[date];
        return sessions.map((session, index) => `
            <tr>
                ${index === 0 ? `<td rowspan="${sessions.length}" style="text-align:center;font-weight:bold">${formatReportDate(date)}</td>` : ''}
                <td style="text-transform:capitalize">${t(`report.sessions.${session.session}`)}</td>
                <td class="text-right">${session.totalWeight.toFixed(2)}</td>
                <td class="text-right">${session.avgFat.toFixed(2)}</td>
                <td class="text-right">${session.ratePerLtr.toFixed(2)}</td>
                <td class="text-right credit">${session.totalAmount.toFixed(2)}</td>
            </tr>
        `).join('');
    }).join('');

    const transRows = borrow.map((transaction, index) => {
        const contribution = getTransactionContribution(transaction.item, transaction.money);
        const colorClass = contribution >= 0 ? 'credit' : 'debit';
        return `
        <tr>
            <td>${transaction.date}</td>
            <td>${getTransactionDesc(transaction)}</td>
            <td class="text-right">${transaction.quantity || '-'}</td>
            <td class="text-right">${transaction.price ? `₹${parseFloat(transaction.price).toFixed(2)}` : '-'}</td>
            <td class="text-right ${colorClass}">${formatSignedCurrency(contribution)}</td>
        </tr>
    `}).join('');

    const finalBalance = printLedger.closingBalance;

    const printHtmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>${t('report.title')}</title>
          ${PRINT_STYLES}
        </head>
        <body>
           <div class="print-header">
                <h1 class="report-title">${t('report.title')}</h1>
                <div class="report-details">
                    <div><b>${t('report.headers.name')}:</b> ${userName || t('balanceSheet.details.not_available')}</div>
                    <div><b>${t('report.headers.mobile')}:</b> ${userMobile || t('balanceSheet.details.not_available')}</div>
                    <div><b>${t('report.headers.period')}:</b> ${formatReportDate(startDate)} &mdash; ${formatReportDate(endDate)}</div>
                </div>
           </div>

           <h2>${t('report.sections.milk')}</h2>
           <table>
               <thead>
                   <tr>
                       <th>${t('report.table.date')}</th>
                       <th>${t('report.table.session')}</th>
                       <th class="text-right">${t('report.table.weight')}</th>
                       <th class="text-right">${t('report.table.fat')}</th>
                       <th class="text-right">${t('report.table.price')}</th>
                       <th class="text-right">${t('report.table.amount')}</th>
                   </tr>
               </thead>
               <tbody>${milkRows}</tbody>
               <tfoot>
                   <tr class="bold-row">
                        <td colspan="2" class="text-center">${t('report.footer.total_milk')}</td>
                        <td class="text-right">${balanceData?.milk?.totalMilk?.toFixed(2) || '0.00'} ${t('balanceSheet.units.ltr')}</td>
                        <td colspan="2"></td>
                        <td class="text-right credit">${balanceData?.milk?.total?.toFixed(2) || '0.00'}</td>
                   </tr>
               </tfoot>
           </table>

           <h2>${t('report.sections.transactions')}</h2>
           <table>
               <thead>
                   <tr>
                       <th>${t('report.table.date')}</th>
                       <th>${t('report.table.transaction')}</th>
                       <th class="text-right">${t('report.table.quantity')}</th>
                       <th class="text-right">${t('report.table.price')}</th>
                       <th class="text-right">${t('report.table.amount')}</th>
                   </tr>
               </thead>
               <tbody>${transRows}</tbody>
           </table>

           <h2>${t('report.sections.summary')}</h2>
           <table>
               <thead>
                   <tr><th>${t('report.table.desc')}</th><th class="text-right">${t('report.table.amount')}</th></tr>
               </thead>
               <tbody>
                   <tr><td>${t('report.summary_rows.prev_bal')}</td><td class="text-right ${printLedger.openingBalance >= 0 ? 'credit' : 'debit'}">${formatSignedCurrency(printLedger.openingBalance)}</td></tr>
                   <tr><td>${t('report.summary_rows.milk_inc')}</td><td class="text-right credit">${formatSignedCurrency(printLedger.contributions.milk)}</td></tr>
                   <tr><td>${t('report.summary_rows.feed_exp')}</td><td class="text-right debit">${formatSignedCurrency(printLedger.contributions.feed)}</td></tr>
                   <tr><td>${t('report.summary_rows.money_rec')}</td><td class="text-right debit">${formatSignedCurrency(printLedger.contributions.moneyReceived)}</td></tr>
                   <tr><td>${t('report.summary_rows.money_giv')}</td><td class="text-right credit">${formatSignedCurrency(printLedger.contributions.moneyGiven)}</td></tr>
                   <tr><td>${t('report.summary_rows.ghee_exp')}</td><td class="text-right debit">${formatSignedCurrency(printLedger.contributions.ghee)}</td></tr>
               </tbody>
               <tfoot>
                   <tr class="bold-row">
                        <td>${t('report.footer.final_bal')}</td>
                        <td class="text-right ${finalBalance >= 0 ? 'credit' : 'debit'}">${formatSignedCurrency(finalBalance)}</td>
                   </tr>
               </tfoot>
           </table>
        </body>
      </html>
    `;
  
    if (hasBridge) {
      window.AndroidBridge.printHtml(printHtmlContent);
    } else {
      const printWindow = window.open('', '_blank');
      printWindow.document.write(printHtmlContent);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    }
  };

  useEffect(() => {
    fetchData();

    return () => {
      requestIdRef.current += 1;
    };
  }, [startDate, endDate, userId]); 

  // Internal Date Handler
  const handleDateSelection = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  const ledger = calculateLedgerBalance({
    openingBalance: balanceData?.Before?.total,
    milk: balanceData?.milk?.total,
    feed: balanceData?.feed?.totalMoney,
    ghee: balanceData?.ghee?.totalMoney,
    moneyReceived: balanceData?.moneyReceived?.totalMoney,
    moneyGiven: balanceData?.moneyGiven?.totalMoney,
  });
  const { openingBalance, closingBalance, contributions } = ledger;
  const sortedDates = Object.keys(milkTableData).sort();

  return (
    <div className="w-full">
      
      {/* 1. EMBEDDED FILTER & ACTION BAR */}
      <div className="bg-white border border-line rounded-2xl p-4 shadow-sm mb-5">
        <div className="flex flex-col md:flex-row gap-4 justify-between md:items-end">
          
          <div className="flex-1 max-w-sm">
            <h2 className="text-[15px] font-bold text-primary mb-3 flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
              {t('report.title')}
            </h2>
            <DateRangeWithLine handleChange={handleDateSelection} />
          </div>

          {balanceData && !loading && (
            <button
              onClick={handlePrint}
              className="flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold py-3 px-5 rounded-xl shadow-sm active:scale-95 transition-all text-[12.5px] h-[46px]"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"></polyline><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path><rect x="6" y="14" width="12" height="8"></rect></svg>
              {t('report.print_btn')}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <LoadingScreen />
      ) : error ? (
        <div className="text-center text-debit py-8 font-bold text-[13px]">{t('report.error_prefix')}: {error}</div>
      ) : !balanceData ? (
        <div className="text-center text-[#6B7280] bg-white border border-dashed border-line rounded-2xl py-12 font-medium text-[13px]">{t('report.select_dates')}</div>
      ) : (
        <div id="printable-area" className="space-y-6">
          
          {/* --- MILK ENTRIES SECTION --- */}
          <div className="bg-white border border-line rounded-2xl shadow-sm overflow-hidden">
            <div className="bg-blue-50/50 px-4 py-3 border-b border-line flex items-center gap-2">
              <span className="text-blue-500 text-lg leading-none">🥛</span>
              <h2 className="text-[12px] font-bold uppercase tracking-widest text-primary">{t('report.sections.milk')}</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-line bg-paper/30">
                    <th className="p-3 text-left text-[9px] font-bold uppercase tracking-widest text-[#6B7280]">{t('report.table.date')}</th>
                    <th className="p-3 text-left text-[9px] font-bold uppercase tracking-widest text-[#6B7280]">{t('report.table.session')}</th>
                    <th className="p-3 text-right text-[9px] font-bold uppercase tracking-widest text-[#6B7280]">{t('report.table.weight')}</th>
                    <th className="p-3 text-right text-[9px] font-bold uppercase tracking-widest text-[#6B7280]">{t('report.table.fat')}</th>
                    <th className="p-3 text-right text-[9px] font-bold uppercase tracking-widest text-[#6B7280]">{t('report.table.price')}</th>
                    <th className="p-3 text-right text-[9px] font-bold uppercase tracking-widest text-[#6B7280]">{t('report.table.amount')}</th>
                  </tr>
                </thead>
                <tbody className="text-[12px] font-medium text-primary divide-y divide-line">
                  {sortedDates.map((date, dateIdx) => {
                    const sessions = milkTableData[date];
                    return sessions.map((session, index) => (
                      <tr key={`${date}-${session.session}`} className="hover:bg-paper/30 transition-colors">
                        {index === 0 ? (
                          <td rowSpan={sessions.length} className="p-3 font-bold border-r border-line bg-paper/10 whitespace-nowrap">
                            {formatReportDate(date)}
                          </td>
                        ) : null}
                        <td className="p-3 capitalize">{t(`report.sessions.${session.session}`)}</td>
                        <td className="p-3 text-right">{session.totalWeight.toFixed(2)}</td>
                        <td className="p-3 text-right text-[#6B7280]">{session.avgFat.toFixed(2)}</td>
                        <td className="p-3 text-right text-[#6B7280]">₹{session.ratePerLtr.toFixed(2)}</td>
                        <td className="p-3 text-right font-mono font-bold text-primary">₹{session.totalAmount.toFixed(2)}</td>
                      </tr>
                    ));
                  })}
                </tbody>
                <tfoot className="bg-paper/80 border-t-2 border-line">
                  <tr>
                    <td colSpan="2" className="p-3 text-[11px] font-bold uppercase tracking-widest text-primary text-center">
                      {t('report.footer.total_milk')}
                    </td>
                    <td className="p-3 text-right font-bold text-[13px] text-primary">
                      {balanceData.milk?.totalMilk?.toFixed(2) || '0.00'} <span className="text-[10px] text-[#6B7280]">{t('balanceSheet.units.ltr')}</span>
                    </td>
                    <td colSpan="2"></td>
                    <td className="p-3 text-right font-mono font-bold text-[14px] text-primary">
                      ₹{balanceData.milk?.total?.toFixed(2) || '0.00'}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* --- TRANSACTIONS SECTION --- */}
          <div className="bg-white border border-line rounded-2xl shadow-sm overflow-hidden">
            <div className="bg-amber-50/50 px-4 py-3 border-b border-line flex items-center gap-2">
              <span className="text-amber-500 text-lg leading-none">💸</span>
              <h2 className="text-[12px] font-bold uppercase tracking-widest text-primary">{t('report.sections.transactions')}</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-line bg-paper/30">
                    <th className="p-3 text-left text-[9px] font-bold uppercase tracking-widest text-[#6B7280]">{t('report.table.date')}</th>
                    <th className="p-3 text-left text-[9px] font-bold uppercase tracking-widest text-[#6B7280]">{t('report.table.transaction')}</th>
                    <th className="p-3 text-right text-[9px] font-bold uppercase tracking-widest text-[#6B7280]">{t('report.table.quantity')}</th>
                    <th className="p-3 text-right text-[9px] font-bold uppercase tracking-widest text-[#6B7280]">{t('report.table.price')}</th>
                    <th className="p-3 text-right text-[9px] font-bold uppercase tracking-widest text-[#6B7280]">{t('report.table.amount')}</th>
                  </tr>
                </thead>
                <tbody className="text-[12px] font-medium text-primary divide-y divide-line">
                  {borrow.length > 0 ? borrow.map((transaction, index) => {
                    const contribution = getTransactionContribution(
                      transaction.item,
                      transaction.money,
                    );
                    const colorClass = contribution >= 0 ? 'text-credit' : 'text-debit';
                    
                    return (
                      <tr key={transaction.id} className="hover:bg-paper/30 transition-colors">
                        <td className="p-3 whitespace-nowrap">{transaction.date}</td>
                        <td className="p-3">{getTransactionDesc(transaction)}</td>
                        <td className="p-3 text-right text-[#6B7280]">{transaction.quantity || '-'}</td>
                        <td className="p-3 text-right text-[#6B7280]">{transaction.price ? `₹${parseFloat(transaction.price).toFixed(2)}` : '-'}</td>
                        <td className={`p-3 text-right font-mono font-bold ${colorClass}`}>
                          {formatSignedCurrency(contribution)}
                        </td>
                      </tr>
                    )
                  }) : (
                    <tr>
                      <td colSpan="5" className="p-6 text-center text-[#6B7280] font-medium text-[13px]">{t('report.empty_transactions')}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* --- ACCOUNT SUMMARY (Receipt Style) --- */}
          <div className="bg-paper border border-line rounded-2xl shadow-sm p-5 mb-8">
            <h2 className="text-[12px] font-bold uppercase tracking-widest text-primary mb-4 text-center border-b border-dashed border-line pb-3">
              {t('report.sections.summary')}
            </h2>
            
            <div className="space-y-3 text-[13px] font-bold text-primary">
              <div className="flex justify-between items-center">
                <span className="text-[#6B7280]">{t('report.summary_rows.prev_bal')}</span>
                <span className={`font-mono ${openingBalance >= 0 ? 'text-credit' : 'text-debit'}`}>
                  {formatSignedCurrency(openingBalance)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[#6B7280]">{t('report.summary_rows.milk_inc')}</span>
                <span className="font-mono text-credit">{formatSignedCurrency(contributions.milk)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[#6B7280]">{t('report.summary_rows.feed_exp')}</span>
                <span className="font-mono text-debit">{formatSignedCurrency(contributions.feed)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[#6B7280]">{t('report.summary_rows.money_rec')}</span>
                <span className="font-mono text-debit">{formatSignedCurrency(contributions.moneyReceived)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[#6B7280]">{t('report.summary_rows.money_giv')}</span>
                <span className="font-mono text-credit">{formatSignedCurrency(contributions.moneyGiven)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[#6B7280]">{t('report.summary_rows.ghee_exp')}</span>
                <span className="font-mono text-debit">{formatSignedCurrency(contributions.ghee)}</span>
              </div>
            </div>

            <div className="mt-5 pt-4 border-t-2 border-primary flex justify-between items-center">
              <span className="text-[14px] font-bold uppercase tracking-widest text-primary">
                {t('report.footer.final_bal')}
              </span>
              <span className={`font-mono font-black text-[22px] px-3 py-1 bg-white border rounded-xl shadow-sm ${closingBalance >= 0 ? 'text-credit border-credit/20' : 'text-debit border-debit/20'}`}>
                {formatSignedCurrency(closingBalance)}
              </span>
            </div>
          </div>

        </div>
      )}
    </div>
  );
};

export default DairyFinancialReport;
